import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import cityExplorerService from '../../services/cityExplorerService';
import PartnerLogo from './PartnerLogo';
import { partnerLogoFor, partnerOrgName } from './partnerLogos';

const LocationCardSkeleton = () => (
  <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Skeleton variant="text" width="55%" height={32} />
    <Skeleton variant="text" width="80%" />
    <Skeleton variant="text" width="40%" />
    <Skeleton variant="rounded" width={120} height={24} sx={{ mt: 2 }} />
  </Paper>
);

const CityExplorerHome = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await cityExplorerService.listLocations();
        if (isMounted) setLocations(data.locations || []);
      } catch (err) {
        if (isMounted) setError(err.message || 'Failed to load locations');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3.5,
          mb: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
        }}
      >
        <Typography variant="overline" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '0.75rem' }}>
          City Explorer
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          What are key enrollment dynamics in our market?
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Explore key population, enrollment, and competitive metrics through a series of interactive
          maps, presentations, and source data files.
        </Typography>
      </Paper>

      {loading && (
        <Grid container spacing={3}>
          {[0, 1, 2].map((index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <LocationCardSkeleton />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && locations.length === 0 && (
        <Alert severity="info">
          No locations have been assigned to your account yet. Reach out to your EDC contact to get access.
        </Alert>
      )}

      {!loading && !error && locations.length > 0 && (
        <Grid container spacing={3}>
          {locations.map((location) => {
            const orgName = partnerOrgName(location);
            return (
            <Grid item xs={12} sm={6} md={4} key={location.id}>
              <Paper
                elevation={0}
                onClick={() => navigate(`/city-explorer/${location.id}`)}
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 16px rgba(67, 56, 202, 0.15)',
                    transform: 'translateY(-2px)',
                    '& .location-name': { color: 'primary.main' },
                    '& .location-chevron': { transform: 'translateX(4px)', color: 'primary.main' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: orgName ? 1 : 1.5 }}>
                  {partnerLogoFor(location.id) ? (
                    <PartnerLogo locationId={location.id} size="title" />
                  ) : (
                    <LocationCityOutlinedIcon color="primary" />
                  )}
                  <Typography
                    variant="h6"
                    className="location-name"
                    sx={{ fontWeight: 600, transition: 'color 0.2s ease-in-out' }}
                  >
                    {location.name}
                  </Typography>
                  <ChevronRightIcon
                    className="location-chevron"
                    sx={{ ml: 'auto', color: 'text.disabled', transition: 'all 0.2s ease-in-out' }}
                  />
                </Box>

                {orgName && (
                  <Typography variant="body2" color="text.secondary">
                    {orgName}
                  </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    size="small"
                    icon={<LayersOutlinedIcon />}
                    label={`${location.categoryCount} map set${location.categoryCount === 1 ? '' : 's'}`}
                    variant="outlined"
                  />
                  {location.county && <Chip size="small" label={location.county} variant="outlined" />}
                </Stack>
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default CityExplorerHome;
